/* eslint-disable */
/* eslint-disable react/jsx-curly-newline */
/* eslint-disable max-len */

import React, { useEffect, useState } from 'react';
import { RegularButton, CommonInput, CommonDropdown, ICommonDropdownOption, FullScreenLoading } from 'pg-ui-library';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useTypedSelector } from 'src/hooks/useTypedSelector';

import { Container, ScreenHeader, CardHeader, LeftHeader, RightHeader, PageTitle } from './createStyles';
import { useQuery } from '@tanstack/react-query';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Chip } from '@mui/material';
import { CellSpan } from './styles';
import { ExpenseStatus } from './enums/expense-status';
import ExpenseService from 'src/services/expense.service';
import { NewFiltersInvoice } from './modals/filtersModal';
import { DateTime } from 'luxon';
import { ExpenseListResponse } from 'src/interfaces';
import { ExpenseFilter } from './modals/filtersExpenseModal';
import { currencySymbol } from 'src/utils/currency';

const ExpenseList: React.FC = () => {
  const [newFilterIsOpen, setNewFilterIsOpen] = useState(false);
  const [filters, setFilters] = useState({
    startDate: null as Date | null,
    endDate: null as Date | null,
    status: '',
    hireType: '',
    expenseShowId: '',
    assignmentId: '',
    clientName: '',
    employeeName: '',
    dateCriteria: '',
    dateFilterType: '',
  });

  const navigate = useNavigate();
  const validationSchema = yup.object({
    subject: yup.string().max(255).required(),
  });

  const statusMapping: Record<
    string,
    { color: 'default' | 'primary' | 'secondary' | 'error' | 'success'; label: string; bgColor: string }
  > = {
    rejected: { color: 'default', label: 'Rejected', bgColor: '#d32f2f' },
    Rejected: { color: 'default', label: 'Rejected', bgColor: '#d32f2f' },
    Reject: { color: 'default', label: 'Rejected', bgColor: '#d32f2f' },

    draft: { color: 'default', label: 'Draft', bgColor: '#546E7A' },
    Draft: { color: 'default', label: 'Draft', bgColor: '#546E7A' },

    submitted: { color: 'default', label: 'Submitted', bgColor: '#0288d1' },
    Submitted: { color: 'default', label: 'Submitted', bgColor: '#0288d1' },

    approved: { color: 'default', label: 'Approved', bgColor: '#F9A825' },
    Approved: { color: 'default', label: 'Approved', bgColor: '#F9A825' },
    'On Hold': { color: 'default', label: 'Approved', bgColor: '#F9A825' },

    bill_processed: { color: 'default', label: 'Bill Processed', bgColor: '#7b1fa2' }, // purple
    Bill_processed: { color: 'default', label: 'Bill Processed', bgColor: '#7b1fa2' },

    pay_processed: { color: 'default', label: 'Pay Processed', bgColor: '#00897b' }, // teal
    Pay_processed: { color: 'default', label: 'Pay Processed', bgColor: '#00897b' },

    completed: { color: 'default', label: 'Completed', bgColor: '#388e3c' },
    resolved: { color: 'default', label: 'Completed', bgColor: '#388e3c' },
    Completed: { color: 'default', label: 'Completed', bgColor: '#388e3c' },
  };

  const allStatusOptions: ICommonDropdownOption[] = Object.values(ExpenseStatus).map((status, index) => ({
    key: String(index),
    label: status.label,
    value: status.value,
  }));

  const { personalInfo } = useTypedSelector(state => state.personalInfo.data);

  const viewInvoice = (row: any) => {
    navigate(`${row.id}`);
  };

  const invoiceColumns: GridColDef[] = [
    {
      headerName: 'ID',
      headerClassName: 'CommonHeaderClass',
      field: 'expenseShowId',
      minWidth: 120,
      renderCell: params => (
        <CellSpan title={params.value} data-tag="allowRowEvents" onClick={() => viewInvoice(params.row)}>
          {params.value}
        </CellSpan>
      ),
    },
    {
      headerName: 'Worker',
      headerClassName: 'CommonHeaderClass',
      field: 'employeeName',
      minWidth: 120,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <CellSpan title={params.value} data-tag="allowRowEvents">
          {params.value}
        </CellSpan>
      ),
    },
    {
      headerName: 'Period Ending',
      headerClassName: 'CommonHeaderClass',
      field: 'weekEnd',
      minWidth: 120,
      renderCell: params => (
        <CellSpan title={params.value} data-tag="allowRowEvents">
          {params.value}
        </CellSpan>
      ),
      sortable: true,
      filterable: true,
    },
    {
      headerName: 'Company',
      headerClassName: 'CommonHeaderClass',
      field: 'companyName',
      minWidth: 120,
      sortable: true,
      filterable: false,
    },
    {
      headerName: 'Client',
      headerClassName: 'CommonHeaderClass',
      field: 'clientName',
      minWidth: 120,
      sortable: true,
      filterable: false,
    },
    {
      headerName: 'Job Title',
      headerClassName: 'CommonHeaderClass',
      field: 'jobTitle',
      minWidth: 150,
      sortable: true,
      filterable: false,
    },
    {
      headerName: 'Hire Type',
      headerClassName: 'CommonHeaderClass',
      field: 'hireType',
      minWidth: 100,
      sortable: true,
      filterable: false,
    },
    {
      headerName: 'Items',
      headerClassName: 'CommonHeaderClass',
      field: 'totalReceipts',
      minWidth: 90,
      sortable: true,
      filterable: false,
    },
    {
      headerName: 'Total Amount',
      headerClassName: 'CommonHeaderClass',
      field: 'totalAmount',
      minWidth: 120,
      sortable: true,
      filterable: false,
      renderCell: params => {
        const amount = params.value;
        const currency = params.row.currency;

        return (
          <CellSpan title={`${currencySymbol(currency)}${amount}`} data-tag="allowRowEvents">
            {currencySymbol(currency)}
            {amount.toFixed(2)}
          </CellSpan>
        );
      },
    },
    {
      headerName: 'Version',
      headerClassName: 'CommonHeaderClass',
      field: 'version',
      minWidth: 80,
      sortable: true,
      filterable: false,
    },
    {
      headerName: 'Status',
      field: 'status',
      width: 140,
      renderCell: params => {
        const status = params.value as string;
        const { label, bgColor } = statusMapping[status] || {
          color: 'default',
          label: 'Unknown',
          bgColor: '#9e9e9e',
        };
        return (
          <Chip
            label={label}
            variant="outlined"
            style={{
              backgroundColor: bgColor,
              fontSize: '12px',
              color: 'white',
              borderColor: 'transparent',
              textTransform: 'capitalize',
              borderRadius: '6px',
            }}
          />
        );
      },
    },
  ];

  const formik = useFormik({
    initialValues: {
      moduleCategory: '',
      subCatergory: '',
      subject: '',
      description: '',
      priority: '',
      attachments: '',
      searchQuery: '',
      invocieCategory: '',
    },
    validationSchema,
    onSubmit: async (values: any) => {
      try {
        console.log('Form Submitted:', values);
      } catch (error) {
        console.error('Error during form submission', error);
      }
    },
  });

  const CreateExpense = () => {
    navigate('/expenses/create');
  };

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState('');
  const [searchTerm, setsearchTerm] = useState('');
  const [sortDirection, setSortDirection] = useState('');
  const [status, setStatus] = useState('');

  const companyId = '';
  const [showFun, setShowFun] = useState(true);
  const [userRole] = useLocalStorage('userRole', null);

  useEffect(() => {
    if (userRole === 'external_manager') {
      setShowFun(false);
    } else {
      setShowFun(true);
    }
  }, [userRole]);

  const formatDateForApi = (date: Date | null) => {
    if (!date) return undefined;
    return DateTime.fromJSDate(date).toFormat('yyyy-MM-dd');
  };

  const apiStartDate = formatDateForApi(filters.startDate);
  const apiEndDate = formatDateForApi(filters.endDate);

  const {
    isFetching: isExpenseFetching,
    data: expenseData,
    refetch: expenseRefetch,
  } = useQuery<{
    items: ExpenseListResponse[];
    meta: { totalItems: number };
  }>(['expense-list', page, limit, searchTerm, sortBy, sortDirection, JSON.stringify(filters)], async () => {
    return ExpenseService.getExpenseList({
      page,
      limit,
      search: searchTerm,
      sortBy,
      sortDirection,
      status: filters.status,
      hireType: filters.hireType,
      expenseShowId: filters.expenseShowId,
      assignmentId: filters.assignmentId,
      clientName: filters.clientName,
      employeeName: filters.employeeName,
      dateCriteria: filters.dateCriteria,
      dateFilterType: filters.dateFilterType,
      fromDate: apiStartDate,
      toDate: apiEndDate,
    });
  });

  // useEffect(() => {
  //   expenseRefetch();
  // }, [page, limit, searchTerm, status, expenseRefetch]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePerRowsChange = async (newLimit: number, newPage: number) => {
    setLimit(newLimit);
    setPage(newPage);
  };

  const onPageMetaChange = async (changes: any) => {
    const persentPage = changes.page + 1;
    if (persentPage > page) {
      handlePageChange(page + 1);
    }
    if (persentPage < page) {
      handlePageChange(page - 1);
    }
    if (changes.pageSize !== limit) {
      handlePerRowsChange(changes.pageSize, page);
    }
  };

  const onSortMetaChange = async (changes: any) => {
    setSortBy(changes[0].field as unknown as string);
    setSortDirection(changes[0].sort);
  };

  const updateFilters = (
    startDate: Date | null,
    endDate: Date | null,
    selectedCompany: any,
    status: string,
    employmentType: string,
    expenseShowId: string,
    assignmentId: string,
    employmentStatus: string,
    clientName: string,
    hireType: string,
    employeeName: string,
    dateCriteria: string,
    dateRangeType: string
  ) => {
    setPage(1);

    setFilters({
      startDate,
      endDate,
      status,
      hireType,
      expenseShowId,
      assignmentId,
      clientName,
      employeeName,
      dateCriteria: dateCriteria || 'period',
      dateFilterType: dateRangeType,
    });
  };

  const formattedData = expenseData?.items?.map((row, index) => ({
    ...row,
  }));

  return (
    <Container>
      <ExpenseFilter
        isOpen={newFilterIsOpen}
        setIsOpen={setNewFilterIsOpen}
        updateFilters={updateFilters}
        statusOptions={allStatusOptions}
      />
      <ScreenHeader>
        <PageTitle>Search Expense</PageTitle>

        <RightHeader style={{ display: 'flex', alignItems: 'center' }}>
          <RegularButton onClick={() => setNewFilterIsOpen(true)}>Filters</RegularButton>

          {showFun && <RegularButton onClick={CreateExpense}>Create Expense</RegularButton>}
        </RightHeader>
      </ScreenHeader>
      <CardHeader>
        <LeftHeader></LeftHeader>
      </CardHeader>
      {isExpenseFetching && <FullScreenLoading />}

      <div>
        {formattedData && formattedData.length > 0 ? (
          <DataGrid
            rows={formattedData}
            columns={invoiceColumns}
            pagination
            paginationMode="server"
            onPaginationModelChange={state => {
              onPageMetaChange(state);
            }}
            onSortModelChange={state => {
              onSortMetaChange(state);
            }}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[10, 20, 50]}
            rowCount={80}
            loading={isExpenseFetching}
            disableColumnFilter
            sx={{
              font: '14px Biotif',
              fontWeight: 'bold',
              '& .CommonHeaderClass': {
                color: '#9399B2',
                font: '16px Biotif',
                fontWeight: 'bold',
              },
              minHeight: '150px',
            }}
          />
        ) : (
          <div style={{ minHeight: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            No records found.
          </div>
        )}
      </div>
    </Container>
  );
};

export default ExpenseList;
